import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';

export default function Dashboard() {
  const [clockedIn, setClockedIn] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [userName, setUserName] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [correctionEntry, setCorrectionEntry] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({requestedClockInTime: '', requestedClockOutTime: '', reason: ''});
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionSuccess, setCorrectionSuccess] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is mapped to an employee first
    api('/me').then(r => r.ok ? r.json() : null).then(user => {
      if (user && user.userId) {
        api('/hcm/employees?q=' + encodeURIComponent(user.userId)).then(r => r.ok ? r.json() : []).then(employees => {
          const userEmployee = employees.find(emp => emp.userId === user.userId);
          if (!userEmployee) {
            // No employee mapping found, redirect to self-mapping
            navigate('/self-map');
            return;
          }
          // User is mapped (either self-mapped or HCM-mapped), proceed with normal loading
          loadActiveEntry();
          loadRecentEntries();
          loadUserName();
          setEmployeeId(userEmployee.id);
          loadCorrections(userEmployee.id);
          setPageLoading(false);
        }).catch(() => {
          // Error loading employees, redirect to self-mapping as fallback
          navigate('/self-map');
        });
      } else {
        setPageLoading(false);
      }
    }).catch(() => {
      setPageLoading(false);
    });
  }, [navigate]);

  const loadUserName = () => {
    const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('displayName='));
    if (match) {
      setUserName(decodeURIComponent(match.split('=')[1]));
    }
  };

  const loadActiveEntry = () => {
    api('/timesheets/clock/active').then(r => {
      if (r.ok) return r.json();
      if (r.status === 404) return null; // Endpoint not implemented yet
      return null;
    }).then(entry => {
      if (entry) {
        setClockedIn(true);
        setActiveEntry(entry);
      }
    }).catch(() => {
      // Silently handle errors for now
    });
  };

  const loadRecentEntries = () => {
    api('/timesheets/clock/recent').then(r => {
      if (r.ok) return r.json();
      if (r.status === 404) return []; // Endpoint not implemented yet
      return [];
    }).then(entries => setRecentEntries(entries.sort((a, b) => new Date(b.startAt) - new Date(a.startAt)))).catch(() => {
      setRecentEntries([]);
    });
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { latitude: position.coords.latitude, longitude: position.coords.longitude };
          setLocation(loc);
          setLocationError('');
          resolve(loc);
        },
        (error) => {
          setLocationError('Unable to get location. Please enable location services.');
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      const loc = await getLocation();
      const res = await api('/timesheets/clock/in', {
        method: 'POST',
        body: JSON.stringify({ latitude: loc.latitude, longitude: loc.longitude }),
      });
      if (res.ok) {
        const entry = await res.json();
        setClockedIn(true);
        setActiveEntry(entry);
      } else if (res.status === 404) {
        setLocationError('Clock functionality is not yet available. Please contact your administrator.');
      } else {
        setLocationError('Failed to clock in. Please try again.');
      }
    } catch (e) {
      // location error already set
    }
    setLoading(false);
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      const loc = await getLocation();
      const res = await api('/timesheets/clock/out', {
        method: 'POST',
        body: JSON.stringify({ latitude: loc.latitude, longitude: loc.longitude }),
      });
      if (res.ok) {
        setClockedIn(false);
        setActiveEntry(null);
        loadRecentEntries();
      } else if (res.status === 404) {
        setLocationError('Clock functionality is not yet available. Please contact your administrator.');
      } else {
        setLocationError('Failed to clock out. Please try again.');
      }
    } catch (e) {
      // location error already set
    }
    setLoading(false);
  };

  const loadCorrections = (empId) => {
    api('/timesheets/time-corrections').then(r => r.ok ? r.json() : []).then(all => {
      setCorrections(all.filter(c => c.employeeId === empId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    }).catch(() => {});
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const toLocalDatetimeStr = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const openCorrection = (entry) => {
    setCorrectionEntry(entry);
    setCorrectionForm({
      requestedClockInTime: toLocalDatetimeStr(entry.startAt),
      requestedClockOutTime: toLocalDatetimeStr(entry.endAt),
      reason: '',
    });
    setCorrectionSuccess(false);
  };

  const handleCorrectionSubmit = async (e) => {
    e.preventDefault();
    setCorrectionSubmitting(true);
    const body = { timesheetEntryId: correctionEntry.id, reason: correctionForm.reason };
    if (correctionForm.requestedClockInTime) body.requestedClockInTime = new Date(correctionForm.requestedClockInTime).toISOString();
    if (correctionForm.requestedClockOutTime) body.requestedClockOutTime = new Date(correctionForm.requestedClockOutTime).toISOString();
    const res = await api('/timesheets/time-corrections', { method: 'POST', body: JSON.stringify(body) });
    setCorrectionSubmitting(false);
    if (res.ok) {
      setCorrectionSuccess(true);
      if (employeeId) loadCorrections(employeeId);
      setTimeout(() => setCorrectionEntry(null), 1500);
    }
  };

  if (pageLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {userName && (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Welcome, {userName}!</h1>
            <p className="text-gray-600 mt-1">Ready to clock in for the day?</p>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow p-8 text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {clockedIn ? 'You are clocked in' : 'You are clocked out'}
          </h2>
          {activeEntry && (
            <p className="text-gray-500 mb-4">Since {formatTime(activeEntry.startAt)}</p>
          )}

          {locationError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{locationError}</div>
          )}

          {location && (
            <p className="text-xs text-gray-400 mb-4">
              📍 {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
            </p>
          )}

          <button
            onClick={clockedIn ? handleClockOut : handleClockIn}
            disabled={loading}
            className={`px-12 py-4 rounded-full text-xl font-bold text-white transition ${
              clockedIn
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Getting location...' : clockedIn ? 'Clock Out' : 'Clock In'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-900">Recent Entries</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">In</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                <th className="px-4 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentEntries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 sm:px-6 py-4 text-gray-900">{e.date}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600">{formatTime(e.startAt)}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600">{formatTime(e.endAt)}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">{e.totalHours != null ? Number(e.totalHours).toFixed(2) : '—'}</td>
                  <td className="px-4 sm:px-6 py-4">
                    <button onClick={() => openCorrection(e)} className="text-amber-600 hover:text-amber-800 text-sm">Request Correction</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recentEntries.length === 0 && (
            <div className="text-center py-12 text-gray-500">No recent entries.</div>
          )}
        </div>

        {corrections.length > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">My Correction Requests</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {corrections.map(c => (
                <div key={c.id} className="px-4 sm:px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-gray-900 font-medium">{c.entryDate}</p>
                      <p className="text-sm text-gray-500 mt-1">Requested: {c.requestedClockInTime ? new Date(c.requestedClockInTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: true}) : '—'} → {c.requestedClockOutTime ? new Date(c.requestedClockOutTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: true}) : '—'}</p>
                      <p className="text-sm text-gray-400 mt-1 italic">"{c.reason}"</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      c.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      c.status === 'DENIED' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>{c.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {correctionEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Request Time Correction</h3>
              <p className="text-sm text-gray-500 mb-4">Entry: {correctionEntry.date} — {formatTime(correctionEntry.startAt)} to {formatTime(correctionEntry.endAt)}</p>
              {correctionSuccess ? (
                <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">Correction request submitted!</div>
              ) : (
                <form onSubmit={handleCorrectionSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Corrected Clock In</label>
                    <input type="datetime-local" value={correctionForm.requestedClockInTime}
                      onChange={e => setCorrectionForm(f => ({...f, requestedClockInTime: e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Corrected Clock Out</label>
                    <input type="datetime-local" value={correctionForm.requestedClockOutTime}
                      onChange={e => setCorrectionForm(f => ({...f, requestedClockOutTime: e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea required value={correctionForm.reason}
                      onChange={e => setCorrectionForm(f => ({...f, reason: e.target.value}))}
                      rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setCorrectionEntry(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button type="submit" disabled={correctionSubmitting}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                      {correctionSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
